/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */

/**
 * Source of web worker.
 * It waits for messages and return the regexp match of pattern to the text.
 * @param {Event} event
 * @param {string} event.text
 * @param {string} event.pattern
 */
const workerSource = `
    onmessage = event => {
        postMessage(event.data.text.match(event.data.pattern));
    };
`;

/**
 * Create local url from source for worker process.
 */
const inlineWorkerUrl = URL.createObjectURL(
    new Blob([ workerSource ], { type: 'text/javascript' })
);

/**
 * Creates new worker process
 * @returns {Worker}
 */
const createWorker = () => new Worker(inlineWorkerUrl);

/**
 * Match function that pass parameters to worker process and waits for its result.
 * @param {object} parameters
 * @param {RegExp} parameters.pattern RegExp object that should be matched
 * @param {string} parameters.text Text that should be matched against the pattern
 * @param {object} parameters.options
 * @param {number} parameters.options.timeout Timeout in miliseconds
 * @param {(err, result) => void} callback
 * @returns {function} - Returns with a terminate function to be able to terminate worker
 */
export default ({pattern, text, options = {}}, callback) => {
    // apply default values
    options = Object.assign({
        timeout: 2000
    }, options);

    let destroy;
    let worker = createWorker();
    let workerTimeout;

    // listen for response
    worker.onmessage = e => {
        destroy();
        if (typeof callback === 'function') {
            callback(null, e.data);
        }
    };

    // send request to the worker
    worker.postMessage({pattern, text});

    // create timeout
    workerTimeout = setTimeout(() => {
        destroy();
        if (typeof callback === 'function') {
            callback(new Error('Timeout'));
        }
    }, options.timeout);

    destroy = () => {
        if (worker) {
            clearTimeout(workerTimeout);
            try {
                worker.terminate();
            } catch(e) {
                // try catch is because jsdom-worker does not support terminate
            }
            worker = null;
        }
    };

    return destroy;
};